import { useMemo } from "react";
import { Tag } from "antd";
import { ReadOnlyEditor } from "components/ReadyOnlyEditor";
import styles from "pages/Operating/Motion/OperatingTools/QBR/QBRTable.module.css";
import { getColorForUserName } from "utils/helpers/getColorForUserName";

// --- Table parsing ---

interface ParsedTable {
  headers: string[];
  rows: string[][];
  hasTable: boolean;
}

function parseTable(rawString: string): ParsedTable {
  if (!rawString) return { headers: [], rows: [], hasTable: false };

  const toHtml = (text: string): string => {
    if (text.includes("<table")) return text;
    const lines = text.split("\n").map((l) => l.trim());
    const tableStart = lines.findIndex(
      (l) => l.startsWith("|") && l.endsWith("|")
    );
    if (tableStart === -1) return text;
    const tableLines: string[] = [];
    for (let i = tableStart; i < lines.length; i++) {
      if (lines[i].startsWith("|") && lines[i].endsWith("|"))
        tableLines.push(lines[i]);
      else if (tableLines.length > 0) break;
    }
    const sepIdx = tableLines.findIndex((l) => /^\|[\s\-:|]+\|$/.test(l));
    if (sepIdx < 1) return text;
    const parseRow = (l: string) =>
      l
        .slice(1, -1)
        .split("|")
        .map((c) => c.trim());
    let html = "<table><thead>";
    tableLines.slice(0, sepIdx).forEach((hl) => {
      html +=
        "<tr>" + parseRow(hl).map((h) => `<th>${h}</th>`).join("") + "</tr>";
    });
    html += "</thead><tbody>";
    tableLines.slice(sepIdx + 1).forEach((dl) => {
      html +=
        "<tr>" + parseRow(dl).map((c) => `<td>${c}</td>`).join("") + "</tr>";
    });
    html += "</tbody></table>";
    return html;
  };

  const htmlString = toHtml(rawString);

  try {
    const stripHtml = (value: string) => {
      const div = document.createElement("div");
      div.innerHTML = value;
      return (div.textContent || "").trim();
    };

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const table = doc.querySelector("table");
    if (!table) return { headers: [], rows: [], hasTable: false };

    const headers = Array.from(table.querySelectorAll("thead th")).map(
      (th) => th.innerHTML
    );
    const bodyRows = Array.from(table.querySelectorAll("tbody tr"));
    const rows =
      bodyRows.length > 0
        ? bodyRows
        : Array.from(table.querySelectorAll("tr")).filter(
            (tr) => tr.querySelectorAll("td").length > 0
          );

    const rowData = rows.map((tr) =>
      Array.from(tr.querySelectorAll("td")).map((td) => td.innerHTML)
    );

    const fallbackHeaders =
      headers.length > 0
        ? headers
        : Array.from(table.querySelectorAll("tr th")).map(
            (th) => th.innerHTML
          );

    const nonEmptyRows = rowData.filter((cells) =>
      cells.some((cell) => stripHtml(cell) !== "")
    );

    let normalizedRows = nonEmptyRows;
    if (fallbackHeaders.length > 0 && rowData.length > 0) {
      const headerText = fallbackHeaders.map(stripHtml);
      const firstRowText = normalizedRows[0]?.map(stripHtml) ?? [];
      const isHeaderRow =
        firstRowText.length === headerText.length &&
        firstRowText.every((cell, idx) => cell === headerText[idx]);
      if (isHeaderRow) normalizedRows = normalizedRows.slice(1);
    }

    return { headers: fallbackHeaders, rows: normalizedRows, hasTable: true };
  } catch {
    return { headers: [], rows: [], hasTable: false };
  }
}

function getPlainText(html: string): string {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").trim();
}

// --- InitialsAvatar ---

export const InitialsAvatar = ({ name }: { name: string }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const bg = getColorForUserName(name);
  return (
    <div
      title={name}
      style={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        background: bg,
        color: "white",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
        cursor: "default"
      }}
    >
      {initials}
    </div>
  );
};

// --- ObjectivesTable ---

interface ObjectivesTableProps {
  text: string;
  emptyMessage?: string;
}

export const ObjectivesTable = ({
  text,
  emptyMessage
}: ObjectivesTableProps) => {
  const parsed = useMemo(() => parseTable(text), [text]);

  if (!parsed.hasTable) {
    return <ReadOnlyEditor content={text || emptyMessage || ""} />;
  }

  // Identify the accountable column (header text = "Accountable" or "A")
  const accountableIdx = parsed.headers.findIndex((h) =>
    /^accountable$|^a$/i.test(getPlainText(h))
  );

  return (
    <div className={`overflow-x-auto ${styles.qbrWrapper}`}>
      <table className={`w-full text-sm ${styles.qbrTable}`}>
        <thead>
          <tr>
            {parsed.headers.map((h, i) => (
              <th
                key={i}
                className={`${styles.qbrHeaderCell} ${i === accountableIdx ? styles.qbrHeaderCellCenter : ""}`}
                dangerouslySetInnerHTML={{ __html: h }}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {parsed.rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => {
                const isAccountable = ci === accountableIdx;
                const isFirst = ci === 0;
                const name = isAccountable ? getPlainText(cell) : "";
                return (
                  <td
                    key={ci}
                    className={`${styles.qbrCell} ${isAccountable ? styles.qbrCellCenter : ""}`}
                  >
                    {isAccountable ? (
                      name ? (
                        <InitialsAvatar name={name} />
                      ) : (
                        <span style={{ color: "#8B869A" }}>–</span>
                      )
                    ) : isFirst ? (
                      <Tag
                        style={{
                          backgroundColor: "#e4e0eb",
                          borderRadius: 999,
                          fontWeight: 600,
                          padding: "2px 10px"
                        }}
                      >
                        {getPlainText(cell)}
                      </Tag>
                    ) : (
                      <span dangerouslySetInnerHTML={{ __html: cell }} />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
